import type { Input } from "../core/Input";
import { MessageBox } from "./MessageBox";
import { Menu, type MenuHooks } from "./Menu";
import { GameWindow } from "./Window";

/**
 * The DOM overlay. Text, menus and screen transitions are HTML rather than
 * rendered geometry — crisper, and far less code than a 3D text system.
 */
export class UI {
  readonly root: HTMLElement;
  readonly message: MessageBox;

  private readonly menus: Menu[] = [];
  private readonly fadeEl: HTMLDivElement;
  private readonly flashEl: HTMLDivElement;
  private readonly hintEl: HTMLDivElement;

  constructor(root: HTMLElement, private input: Input, private menuHooks: MenuHooks = {}) {
    this.root = root;
    this.message = new MessageBox(root, input);

    this.fadeEl = document.createElement("div");
    this.fadeEl.id = "ds-fade";
    this.flashEl = document.createElement("div");
    this.flashEl.id = "ds-flash";
    this.hintEl = document.createElement("div");
    this.hintEl.id = "ds-hint";
    root.append(this.fadeEl, this.flashEl, this.hintEl);
  }

  setMenuHooks(hooks: MenuHooks): void {
    this.menuHooks = hooks;
    for (const menu of this.menus) Object.assign(menu, { hooks });
  }

  update(dt: number): void {
    this.message.update(dt);
    // Newest menu first: only the topmost open menu should take input.
    for (let i = this.menus.length - 1; i >= 0; i--) {
      const menu = this.menus[i];
      if (menu.busy) {
        menu.update(dt);
        break;
      }
    }
  }

  /** Grabs an idle menu from the pool, creating one if they are all in use. */
  menu(): Menu {
    let menu = this.menus.find((m) => !m.busy);
    if (!menu) {
      menu = new Menu(this.root, this.input, this.menuHooks);
      this.menus.push(menu);
    }
    return menu;
  }

  window(className = "", id?: string): GameWindow {
    return new GameWindow(this.root, className, id);
  }

  setHint(text: string): void {
    this.hintEl.textContent = text;
  }

  // ------------------------------------------------------------ transitions

  fade(opacity: number, ms = 300): Promise<void> {
    this.fadeEl.style.transitionDuration = `${ms}ms`;
    // Force a reflow so consecutive fades in the same frame still animate.
    void this.fadeEl.offsetHeight;
    this.fadeEl.style.opacity = String(opacity);
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  fadeOut(ms = 300): Promise<void> {
    return this.fade(1, ms);
  }

  fadeIn(ms = 300): Promise<void> {
    return this.fade(0, ms);
  }

  /** Hard white flash — the classic "a monster appeared" punctuation. */
  async flash(times = 3, ms = 70): Promise<void> {
    for (let i = 0; i < times; i++) {
      this.flashEl.style.opacity = "0.85";
      await delay(ms);
      this.flashEl.style.opacity = "0";
      await delay(ms);
    }
  }
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
